import * as React from "react";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { Controller } from "react-hook-form";
import type {
    Control,
    FieldPath,
    FieldValues,
    RegisterOptions,
} from "react-hook-form";
import { cn } from "@/shared/lib/utils";
import { Label } from "@/shared/ui/components/ui/label";
import { Select } from "@/shared/ui/components/custom/Select";
import { useTranslation } from "react-i18next";

interface CalendarProps {
    selectedDate?: Date;
    onSelectDate: (date: Date) => void;
    onClose: () => void;
    minDate?: Date;
    maxDate?: Date;
}

const Calendar = React.memo(function Calendar({ selectedDate, onSelectDate, onClose, minDate, maxDate }: CalendarProps) {
    const [currentMonth, setCurrentMonth] = React.useState(
        selectedDate ? selectedDate.getMonth() : new Date().getMonth()
    );
    const [currentYear, setCurrentYear] = React.useState(
        selectedDate ? selectedDate.getFullYear() : new Date().getFullYear()
    );
    const { t, i18n } = useTranslation();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

    const monthLabels = React.useMemo(
        () => Array.from({ length: 12 }, (_, i) => t(`datePicker.months.${i}`)),
        [t]
    );
    const daysOfWeekLabels = React.useMemo(
        () => Array.from({ length: 7 }, (_, i) => t(`datePicker.daysOfWeek.${i}`)),
        [t]
    );

    const handlePrevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const handleDateClick = (day: number) => {
        const selected = new Date(currentYear, currentMonth, day);
        onSelectDate(selected);
        onClose();
    };

    const isSelectedDate = (day: number) => {
        if (!selectedDate) return false;
        return (
            selectedDate.getDate() === day &&
            selectedDate.getMonth() === currentMonth &&
            selectedDate.getFullYear() === currentYear
        );
    };

    const isBeforeMinDate = (day: number) => {
        if (!minDate) return false;
        const date = new Date(currentYear, currentMonth, day);
        const min = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
        return date < min;
    };

    const isAfterMaxDate = (day: number) => {
        if (!maxDate) return false;
        const date = new Date(currentYear, currentMonth, day);
        const max = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
        return date > max;
    };

    const yearOptions = React.useMemo(() => {
        const currentYearValue = new Date().getFullYear();
        const startYear = currentYearValue - 10;
        const endYear = currentYearValue + 5;
        const years = [];
        for (let i = startYear; i <= endYear; i++) {
            years.push({ value: String(i), label: String(i) });
        }
        return years;
    }, []);

    const monthOptions = React.useMemo(
        () => monthLabels.map((label, index) => ({ value: String(index), label })),
        [monthLabels]
    );

    return (
        <div className="bg-white border border-grey-200 rounded-lg shadow-lg p-4 w-fit">
            <div className="flex items-center justify-between mb-4">
                <button
                    type="button"
                    onClick={handlePrevMonth}
                    className="p-1 hover:bg-grey-50 rounded"
                >
                    {i18n.language === "ar" ? <ArrowRight className="h-4 w-6" /> : <ArrowLeft className="h-4 w-6" />}

                </button>

                <div className="flex items-center gap-3">
                    <Select
                        value={String(currentMonth)}
                        onChange={(v) => setCurrentMonth(Number(v))}
                        options={monthOptions}
                        className="!py-2 !px-3 gap-6 min-w-[120px] text-sm font-medium"
                        containerClassName="max-h-[200px] overflow-y-auto"
                    />
                    <Select
                        value={String(currentYear)}
                        onChange={(v) => setCurrentYear(Number(v))}
                        options={yearOptions}
                        className="!py-2 !px-3 gap-6 min-w-[120px] text-sm font-medium"
                        containerClassName="max-h-[200px] overflow-y-auto"
                    />
                </div>

                <button
                    type="button"
                    onClick={handleNextMonth}
                    className="p-1 hover:bg-grey-50 rounded"
                >
                    {i18n.language === "en" ? <ArrowRight className="h-4 w-6" /> : <ArrowLeft className="h-4 w-6" />}
                </button>
            </div>

            <div className="grid grid-cols-7 gap-3 mb-2 border-b border-grey-100 pb-2">
                {daysOfWeekLabels.map((dayLabel, index) => (
                    <div
                        key={index}
                        className="text-center text-xxs font-medium text-[#7C7C7C] py-2"
                    >
                        {dayLabel}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-3">
                {Array.from({ length: firstDayOfMonth }).map((_, index) => (
                    <div key={`empty-${index}`} />
                ))}
                {Array.from({ length: daysInMonth }).map((_, index) => {
                    const day = index + 1;
                    const isSelected = isSelectedDate(day);
                    const isDisabled = isBeforeMinDate(day) || isAfterMaxDate(day);
                    return (
                        <button
                            key={day}
                            type="button"
                            onClick={() => !isDisabled && handleDateClick(day)}
                            disabled={isDisabled}
                            className={cn(
                                "h-9 w-9 rounded-full text-sm font-medium  transition-colors",
                                "hover:bg-grey-50",
                                isSelected &&
                                "bg-brand-green text-white hover:bg-brand-green",
                                !isSelected && !isDisabled && "text-grey-900",
                                isDisabled && "text-grey-200 cursor-not-allowed hover:bg-transparent"
                            )}
                        >
                            {day.toString().padStart(2, "0")}
                        </button>
                    );
                })}
            </div>
        </div>
    );
});

export type DatePickerPosition = "top" | "bottom" | "left" | "right" | "center" | "bottom-center";

export interface DatePickerBaseProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
    label?: React.ReactNode;
    hint?: string;
    error?: string;
    value?: Date | string;
    onChange?: (date: Date | undefined) => void;
    calendarPosition?: DatePickerPosition;
    darkModeOnOpen?: boolean;
    leftIcon?: React.ReactNode;
    minDate?: Date;
    maxDate?: Date;
    forceCloseKey?: string | number;
}

const DatePickerBase = React.forwardRef<HTMLInputElement, DatePickerBaseProps>(
    ({ className, label, hint, error, disabled, id, value, onChange, calendarPosition: calendarPositionProp = "bottom-center", darkModeOnOpen = false, leftIcon, minDate, maxDate, forceCloseKey, ...props }, ref) => {
        const inputId = id || React.useId();
        const hasError = Boolean(error);
        const [isOpen, setIsOpen] = React.useState(false);
        const containerRef = React.useRef<HTMLDivElement>(null);

        const selectedDate = React.useMemo(() => {
            if (!value) return undefined;
            return typeof value === "string" ? new Date(value) : value;
        }, [value]);

        const hasValue = Boolean(selectedDate);

        const formatDate = (date: Date | undefined) => {
            if (!date) return "";
            const day = date.getDate().toString().padStart(2, "0");
            const month = (date.getMonth() + 1).toString().padStart(2, "0");
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        };

        const handleSelectDate = (date: Date) => {
            onChange?.(date);
            setIsOpen(false);
        };

        const [calendarPosition, setCalendarPosition] = React.useState<DatePickerPosition>(
            calendarPositionProp
        );

        React.useEffect(() => {
            if (forceCloseKey !== undefined) {
                setIsOpen(false);
            }
        }, [forceCloseKey]);

        React.useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (!containerRef.current?.contains(event.target as Node)) {
                    const target = event.target as Node;
                    const el = target instanceof Element ? target : target.parentElement;
                    if (el?.closest?.("[data-radix-select-content], [role='listbox']")) return;
                    setIsOpen(false);
                }
            };

            if (isOpen) {
                document.addEventListener("mousedown", handleClickOutside);

                requestAnimationFrame(() => {
                    if (!containerRef.current) return;

                    const rect = containerRef.current.getBoundingClientRect();
                    const calendarWidth = 336;
                    const calendarHeight = 300;
                    const viewportWidth = window.innerWidth;
                    const viewportHeight = window.innerHeight;

                    let adjustedPosition = calendarPositionProp;

                    if (rect.right + calendarWidth > viewportWidth && rect.left - calendarWidth < 0) {
                        adjustedPosition = "bottom";
                    } else if (rect.right + calendarWidth > viewportWidth) {
                        if (calendarPositionProp === "right") {
                            adjustedPosition = "left";
                        } else if (calendarPositionProp === "bottom" || calendarPositionProp === "center") {
                            adjustedPosition = "bottom";
                        }
                    }

                    if (rect.bottom + calendarHeight > viewportHeight && rect.top - calendarHeight < 0) {
                        adjustedPosition = "bottom";
                    } else if (rect.bottom + calendarHeight > viewportHeight) {
                        if (calendarPositionProp === "bottom" || calendarPositionProp === "center") {
                            adjustedPosition = "top";
                        }
                    }

                    setCalendarPosition(adjustedPosition);
                });
            }

            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [isOpen, calendarPositionProp]);

        return (
            <div className="w-full gap-3 ">
                {label && (
                    <Label
                        htmlFor={inputId}
                        className={cn(
                            "mb-3 block text-sm font-medium text-grey-900",
                            disabled && "text-grey-200",
                            hasError && "text-error-700"
                        )}
                    >
                        {label}
                    </Label>
                )}

                <div className="relative" ref={containerRef}>
                    <div className="relative">


                        {leftIcon && (
                            <div className="absolute start-4 top-1/2 -translate-y-1/2 pointer-events-none text-grey-400">
                                {leftIcon}
                            </div>
                        )}
                        <input
                            id={inputId}
                            ref={ref}
                            type="text"
                            readOnly
                            disabled={disabled}
                            value={formatDate(selectedDate)}
                            onClick={() => !disabled && setIsOpen(!isOpen)}
                            placeholder="DD/MM/YYYY"
                            className={cn(
                                "flex h-12 w-full rounded-lg border px-4  text-sm font-normal shadow-none transition-colors",
                                "pe-12",
                                leftIcon && "ps-10",
                                "focus-visible:outline-none focus-visible:ring-0",
                                "disabled:cursor-not-allowed disabled:opacity-100",
                                "disabled:bg-grey-50 disabled:border-grey-100 disabled:text-grey-200",
                                "hover:border-brand-green cursor-pointer",
                                darkModeOnOpen &&
                                isOpen &&
                                "bg-grey-900 text-white border-grey-900 placeholder:text-white focus-visible:border-grey-900",
                                !(darkModeOnOpen && isOpen) &&
                                "bg-white hover:border-brand-green focus-visible:border-brand-green",
                                !(darkModeOnOpen && isOpen) &&
                                !hasError &&
                                !hasValue &&
                                "border-grey-100 placeholder:text-grey-200 text-grey-200 hover:text-grey-200 focus-visible:text-grey-900",
                                !(darkModeOnOpen && isOpen) &&
                                !hasError &&
                                hasValue &&
                                " border-grey-900 text-basic-black focus-visible:text-grey-900 font-bold",
                                hasError &&
                                "bg-error-bg border-error-700 text-error-700 placeholder:text-error-700 hover:border-error-700 focus-visible:border-error-700 focus-visible:text-error-700",
                                className
                            )}
                            {...props}
                        />
                        <div
                            className={cn(
                                "absolute end-5 top-1/2 -translate-y-1/2 transition-transform duration-200",
                                isOpen && "rotate-180",
                                darkModeOnOpen && isOpen && "text-white",
                                !(darkModeOnOpen && isOpen) &&
                                !hasError &&
                                !hasValue &&
                                "text-grey-200",
                                !(darkModeOnOpen && isOpen) &&
                                !hasError &&
                                hasValue &&
                                "text-grey-900 font-bold",
                                hasError && "text-error-700"
                            )}
                        >
                            <ChevronDown className="h-5 w-5" />
                        </div>
                    </div>

                    {isOpen && (
                        <div
                            className={cn(
                                "absolute z-50",
                                calendarPosition === "top" && "bottom-full mb-2",
                                calendarPosition === "bottom" && "top-full mt-2",
                                calendarPosition === "left" && "right-full mr-2 top-0",
                                calendarPosition === "right" && "left-full ml-2 top-0",
                                calendarPosition === "center" && "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
                                calendarPosition === "bottom-center" && "top-full mt-2 left-1/2 -translate-x-1/2",
                                // Smart horizontal alignment - align left to prevent going off right edge
                                (calendarPosition === "top" || calendarPosition === "bottom") &&
                                "left-0"
                            )}
                        >
                            <Calendar
                                selectedDate={selectedDate}
                                onSelectDate={handleSelectDate}
                                onClose={() => setIsOpen(false)}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                    )}
                </div>

                {(hint || error) && (
                    <p
                        className={cn(
                            "mt-3 text-xs font-medium text-grey-300",
                            hasError && "text-error-700"
                        )}
                    >
                        {error || hint}
                    </p>
                )}
            </div>
        );
    }
);

DatePickerBase.displayName = "DatePickerBase";

export interface DatePickerProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<DatePickerBaseProps, "name" | "value" | "onChange" | "error"> {
    name?: TName;
    control?: Control<TFieldValues>;
    rules?: Omit<
        RegisterOptions<TFieldValues, TName>,
        "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
    >;
    error?: string;
    calendarPosition?: DatePickerPosition;
}

function DatePicker<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
    props: DatePickerProps<TFieldValues, TName> & {
        ref?: React.Ref<HTMLInputElement>;
    }
) {
    const { name, control, rules, error: errorProp, ...restProps } = props;

    if (control && name) {
        return (
            <Controller<TFieldValues, TName>
                name={name}
                control={control as any}
                rules={rules as any}
                render={({ field, fieldState }) => (
                    <DatePickerBase
                        {...restProps}
                        {...field}
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState.error?.message || errorProp}
                    />
                )}
            />
        );
    }

    return <DatePickerBase {...restProps} error={errorProp} />;
}

export { DatePicker, DatePickerBase };

